import { StatProps, UsdPriceTextWithTooltip } from "@bridgesplit/ui";
import { formatPercent } from "@bridgesplit/utils";
import { LoansAggregate } from "@bridgesplit/abf-react";
import { COPY } from "app/constants";

import { useActivePortfolioRewardsSummary } from "./util";
import { PortfolioTableStats, RewardPoint } from "./common";

export default function BorrowLoanStats({ aggregate }: { aggregate: LoansAggregate | undefined }) {
    const portfolioRewards = useActivePortfolioRewardsSummary();
    const loading = !aggregate || !portfolioRewards || portfolioRewards.isLoading;

    const totalBorrowedUsd = aggregate ? aggregate.principalUsd + aggregate.interestAccruedUsd : undefined;
    const netPositionUsd =
        aggregate && totalBorrowedUsd !== undefined
            ? Math.max(aggregate.collateralUsd - totalBorrowedUsd, 0)
            : undefined;
    // Borrower daily net = collateral yield − debt interest − principal yield foregone.
    // `dailyPrincipalYieldUsd` is 0 for non-yield-bearing principals (USDC) and accounts for
    // the appreciation the borrower owes on a yield-bearing borrowed asset (JLP, mSOL).
    const dailyNetInterest = aggregate
        ? aggregate.dailyCollateralYieldUsd - aggregate.dailyInterestUsd - aggregate.dailyPrincipalYieldUsd
        : undefined;

    const stats: StatProps[] = [
        {
            value: (
                <UsdPriceTextWithTooltip textColor="body" rawUsdValue={aggregate?.collateralUsd} loading={!aggregate} />
            ),
            caption: COPY.TOTAL_COLLATERAL_TERM
        },
        {
            value: <UsdPriceTextWithTooltip textColor="body" rawUsdValue={totalBorrowedUsd} loading={!aggregate} />,
            caption: COPY.TOTAL_BORROWED_TERM
        },
        {
            value: <UsdPriceTextWithTooltip textColor="body" rawUsdValue={netPositionUsd} loading={!aggregate} />,
            caption: COPY.NET_VALUE_TERM
        },
        {
            value: formatPercent(aggregate?.wAvgApy),
            caption: COPY.AVG_APY_TERM,
            tooltip: COPY.AVG_APY_TOOLTIP
        },
        {
            value: <UsdPriceTextWithTooltip textColor="body" rawUsdValue={dailyNetInterest} loading={!aggregate} />,
            caption: COPY.NET_INCOME_TERM,
            tooltip: COPY.SECTION_DAILY_INTEREST_TOOLTIP
        },
        {
            value: <RewardPoint points={portfolioRewards?.data?.activeBorrowDailyPoints ?? 0} variant="body1" />,
            caption: COPY.DAILY_REWARDS_TERM
        }
    ];

    return <PortfolioTableStats stats={stats} loading={loading} />;
}
